import axios from 'axios';
import React from 'react';
import Div100vh from 'react-div-100vh';
import { trackAnalyticsEvent } from '../../../analytics/analytics';
import { t } from '../../../utils/i18n';
import DivButton from '../../a11y/DivButton';
import FadeAnimation from '../../animations/FadeAnimation';
import ButtonText from '../../elements/ButtonText';
import AnimatedTimMessage from '../../tim/AnimatedTimMessage';
import DelayRender from '../DelayRender';
import IconText from '../IconText';
import ModalNavbar from '../ModalNavbar';
import Spacing from '../Spacing';
import EmailCard from './EmailCard';

interface TripNotAvailableModalContentProps {
  close: () => void;
  analyticsCategory: string;
  memoryOrDestinationTitle: string;
}

const renderCloseButton = (close: () => void, emailReceived: boolean) => {
  return (
    <DivButton onClick={close} data-cy="trip-not-available-modal-close-button">
      <IconText icon="travel-no" iconColor="purple-light">
        <ButtonText variant="primary">
          {emailReceived ? t('Sulje') : t('Ei kiitos')}
        </ButtonText>
      </IconText>
    </DivButton>
  );
};

const notifyWhenTripAvailableApiUrl = '/api/notify-when-trip-available';

const TripNotAvailableModalContent: React.FunctionComponent<TripNotAvailableModalContentProps> =
  ({ close, memoryOrDestinationTitle, analyticsCategory }) => {
    const [timTalking, setTimTalking] = React.useState<boolean>(true);
    const [emailReceived, setEmailReceived] = React.useState<boolean>(false);
    const secondTimMessage = t(
      'Mutta ei hätää! Kyllä me vielä reissaamme! Jätä alle sähköinen postiosoitteesi, niin otan sinuun yhteyttä heti kun sen aika koittaa 🏝️😜'
    );
    const sendClicked = async (email: string) => {
      setEmailReceived(true);
      await axios.post(notifyWhenTripAvailableApiUrl, {
        email,
        tripName: memoryOrDestinationTitle
      });
      trackAnalyticsEvent(
        analyticsCategory,
        'Lähetetty sähköposti "Matkaa ei saatavilla" -modaalissa',
        memoryOrDestinationTitle
      );
    };
    return (
      <Div100vh>
        <div
          className="trip-not-available-modal-content"
          data-cy="trip-not-available-modal-content"
        >
          <ModalNavbar title={t('Tim')} closeDialog={close} />
          <Spacing right={2} left={2}>
            <Spacing top={3} bottom={3}>
              <AnimatedTimMessage showIcon iconActive talking={timTalking}>
                {t('Tämä matka ei tosiaan ole vielä saatavilla')}{' '}
                <i>{t('vallitsevan maailman tilanteen takia…')}</i> 😢
              </AnimatedTimMessage>
            </Spacing>
            {/* Duration is set based on how the animations look together */}
            <DelayRender duration={2500}>
              <AnimatedTimMessage
                onAnimationDone={() => {
                  setTimTalking(false);
                }}
              >
                {secondTimMessage}
              </AnimatedTimMessage>
            </DelayRender>
          </Spacing>
          <Spacing top="auto" right={2} bottom={4} left={2}>
            {/*Duration is set based on how the animations look together */}
            <DelayRender duration={6500}>
              <FadeAnimation>
                <div className="bottom">
                  <EmailCard onSendClicked={sendClicked} />
                  <Spacing top={3} right={1}>
                    {renderCloseButton(close, emailReceived)}
                  </Spacing>
                </div>
              </FadeAnimation>
            </DelayRender>
          </Spacing>
        </div>
        {/*language=CSS*/}
        <style jsx>{`
          .trip-not-available-modal-content {
            display: flex;
            flex-direction: column;
            height: 100%;
          }
          .bottom {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
          }
          // Stretch first element to 100% width (email card)
          .bottom > :global(div:first-child) {
            width: 100%;
          }
        `}</style>
      </Div100vh>
    );
  };

export default TripNotAvailableModalContent;
